import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types"; // Import PropTypes for prop validation
import { createPortal } from "react-dom";
import "./DropdownWrapper.scss";

// Custom hook for managing the dropdown's state and position
const useDropdown = (direction = "right", matchToggleWidth = false) => {
  const [isOpen, setIsOpen] = useState(false); // Tracks whether the dropdown is open or closed
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 }); // Stores dropdown position
  const dropdownRef = useRef(null); // Ref for the dropdown content
  const toggleElementRef = useRef(null); // Ref for the element that triggers the dropdown

  // Function to toggle the dropdown's open/closed state
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  // Function to close the dropdown
  const closeDropdown = () => {
    setIsOpen(false);
  };

  // Handles clicks outside of the dropdown to close it
  const handleOutsideClick = (e) => {
    if (e.target.closest?.(".modal, .modal-backdrop, .center-model-popup")) {
      return;
    }
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target) &&
      toggleElementRef.current &&
      !toggleElementRef.current.contains(e.target)
    ) {
      closeDropdown();
    }
  };

  // Updates the dropdown position to avoid overflow on the screen
  const updateDropdownPosition = () => {
    if (toggleElementRef.current && dropdownRef.current) {
      const toggleRect = toggleElementRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();

      const gap = 10; // Add a 10px gap

      let top = toggleRect.bottom + gap; // Position below the trigger element
      let left = toggleRect.left; // Position aligned with the left of the trigger element

      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Handle overflow by switching position
      if (direction === "right") {
        if (left + dropdownRect.width > windowWidth) {
          left = toggleRect.right - dropdownRect.width; // Flip to left if overflow
        }
      } else if (direction === "left") {
        left = toggleRect.right - dropdownRect.width;
        if (left < 0) {
          left = toggleRect.left; // Flip to right if overflow
        }
      }

      if (top + dropdownRect.height > windowHeight) {
        top = toggleRect.top - dropdownRect.height - gap; // Position above the element if no space below
      }

      if (top < 0) {
        top = toggleRect.bottom + gap; // Reset to below if no space above
      }

      setDropdownPosition({
        top,
        left,
        width: matchToggleWidth ? toggleRect.width : 0,
      });
    }
  };

  // Event listener to close dropdown if clicked outside
  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update dropdown position when it is opened or when scrolling occurs
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();

      // Add scroll event listener to update position dynamically
      const handleScroll = () => {
        updateDropdownPosition();
      };

      window.addEventListener("scroll", handleScroll, true);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Update position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        updateDropdownPosition();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Close dropdown if the parent element goes out of view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          closeDropdown(); // Close the dropdown if the parent is not visible
        }
      },
      { threshold: 0.1 } // Trigger when at least 10% of the element is visible
    );

    if (toggleElementRef.current) {
      observer.observe(toggleElementRef.current);
    }

    return () => {
      if (toggleElementRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(toggleElementRef.current);
      }
    };
  }, []);

  // Return values and functions to control dropdown behavior
  return {
    isOpen,
    toggleDropdown,
    closeDropdown,
    dropdownRef,
    toggleElementRef,
    dropdownPosition,
  };
};

// DropdownWrapper component to render the dropdown toggle button and dropdown content
const DropdownWrapper = ({
  children,
  toggleElement,
  className = "",
  direction = "right",
  closeOnSelect = false, // New prop to close dropdown when an item is clicked
  matchToggleWidth = false,
}) => {
  const {
    isOpen,
    toggleDropdown,
    dropdownRef,
    toggleElementRef,
    dropdownPosition,
    closeDropdown,
  } = useDropdown(direction, matchToggleWidth);

  // Handle click on children to close the dropdown
  const handleChildClick = () => {
    closeDropdown(); // Close the dropdown when any child is clicked
  };

  return (
    <div className="dropdown-wrapper">
      <button
        ref={toggleElementRef}
        onClick={toggleDropdown}
        className="dropdownwrapper-toggle"
      >
        {toggleElement}
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className={`dropdownwrapper-content ${className}`}
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              ...(matchToggleWidth && dropdownPosition.width
                ? { minWidth: dropdownPosition.width }
                : {}),
            }}
            onClick={closeOnSelect ? handleChildClick : undefined} // Apply onClick only when closeOnSelect is true
          >
            <div>{children}</div>
          </div>,
          document.body
        )}
    </div>
  );
};

// PropTypes validation for DropdownWrapper
DropdownWrapper.propTypes = {
  children: PropTypes.node.isRequired, // Dropdown content (can be any renderable React node)
  toggleElement: PropTypes.node.isRequired, // Element that triggers the dropdown
  direction: PropTypes.oneOf(["left", "right"]), // Direction the dropdown should open
  closeOnSelect: PropTypes.bool,
  matchToggleWidth: PropTypes.bool,
};

DropdownWrapper.defaultProps = {
  direction: "right", // Default to opening to the right
  matchToggleWidth: false,
};

// Export the hook and component
export { useDropdown, DropdownWrapper };
